import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { BatteryFull, Check, Mic, Plus, Signal, Wifi } from "lucide-react";

const Typing = () => (
    <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="self-end bg-cyan-500/15 border border-cyan-400/25 rounded-2xl rounded-br-md px-3.5 py-3 flex gap-1 w-fit"
        data-testid="phone-typing-indicator"
    >
        {[0, 1, 2].map((i) => (
            <span key={i} className="typing-dot w-1.5 h-1.5 rounded-full bg-cyan-300" style={{ animationDelay: `${i * 0.15}s` }} />
        ))}
    </motion.div>
);

const Bubble = ({ m }) => {
    if (m.type === "system") {
        return (
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="self-center">
                <span className="ff-mono text-[9px] uppercase tracking-[0.18em] text-slate-400 bg-white/5 border border-white/10 rounded-full px-3 py-1">
                    {m.text}
                </span>
            </motion.div>
        );
    }
    if (m.type === "badge") {
        return (
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="self-center" data-testid="phone-success-badge">
                <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-emerald-300 bg-emerald-400/10 border border-emerald-400/30 rounded-full px-3 py-1.5">
                    <Check className="w-3 h-3" />
                    {m.text}
                </span>
            </motion.div>
        );
    }
    const ai = m.from === "ai";
    return (
        <motion.div
            initial={{ opacity: 0, y: 12, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className={`max-w-[82%] px-3.5 py-2.5 text-[12.5px] leading-relaxed rounded-2xl ${
                ai
                    ? "self-end bg-gradient-to-br from-cyan-500 to-blue-600 text-white rounded-br-md shadow-[0_8px_20px_-8px_rgba(0,240,255,0.4)]"
                    : "self-start bg-white/10 border border-white/10 text-slate-200 rounded-bl-md"
            }`}
        >
            {m.text}
        </motion.div>
    );
};

export const PhoneBadge = ({ icon: Icon, title, sub, className = "", delay = 0 }) => (
    <motion.div
        initial={{ opacity: 0, y: 18, scale: 0.9 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ delay, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className={`absolute z-20 ${className}`}
    >
        <motion.div
            animate={{ y: [-6, 6, -6] }}
            transition={{ duration: 5.5, repeat: Infinity, ease: "easeInOut", delay }}
            className="glass rounded-2xl px-4 py-3 flex items-center gap-3 shadow-[0_20px_40px_-12px_rgba(0,0,0,0.6)]"
        >
            <div className="w-8 h-8 rounded-xl bg-cyan-400/15 border border-cyan-400/30 flex items-center justify-center shrink-0">
                <Icon className="w-4 h-4 text-cyan-300" />
            </div>
            <div>
                <p className="text-xs font-semibold text-white whitespace-nowrap">{title}</p>
                {sub && <p className="text-[10px] text-slate-400 whitespace-nowrap">{sub}</p>}
            </div>
        </motion.div>
    </motion.div>
);

export const Phone = ({ script, loop = true, headerTitle = "AI Employee", className = "", testid = "phone-mockup" }) => {
    const [msgs, setMsgs] = useState([]);
    const [typing, setTyping] = useState(false);
    const scrollRef = useRef(null);

    useEffect(() => {
        let alive = true;
        let timers = [];
        setMsgs([]);
        setTyping(false);
        const schedule = (fn, d) => timers.push(setTimeout(() => alive && fn(), d));
        const run = (i) => {
            if (!alive) return;
            if (i >= script.length) {
                if (loop) schedule(() => { setMsgs([]); run(0); }, 5200);
                return;
            }
            const s = script[i];
            if (s.from === "ai") {
                setTyping(true);
                schedule(() => {
                    setTyping(false);
                    setMsgs((m) => [...m, s]);
                    run(i + 1);
                }, Math.min(900 + s.text.length * 13, 2300));
            } else {
                schedule(() => {
                    setMsgs((m) => [...m, s]);
                    run(i + 1);
                }, s.from === "customer" ? 1500 : 1000);
            }
        };
        schedule(() => run(0), 700);
        return () => {
            alive = false;
            timers.forEach(clearTimeout);
        };
    }, [script, loop]);

    useEffect(() => {
        const el = scrollRef.current;
        if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    }, [msgs, typing]);

    return (
        <div className={`relative ${className}`} data-testid={testid}>
            <div className="absolute -inset-10 bg-cyan-500/10 blur-[80px] rounded-full pointer-events-none" />
            <motion.div
                animate={{ y: [-8, 8, -8] }}
                transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
                className="relative"
            >
                <div className="relative w-[290px] sm:w-[320px] rounded-[2.9rem] border border-white/15 bg-gradient-to-b from-slate-700/80 via-slate-800 to-slate-900 p-[10px] shadow-[0_50px_100px_-20px_rgba(0,0,0,0.8),0_0_60px_-20px_rgba(0,240,255,0.25)]">
                    <div className="absolute -left-[2px] top-24 w-[3px] h-8 bg-slate-600 rounded-full" />
                    <div className="absolute -left-[2px] top-36 w-[3px] h-12 bg-slate-600 rounded-full" />
                    <div className="absolute -right-[2px] top-28 w-[3px] h-16 bg-slate-600 rounded-full" />
                    <div className="rounded-[2.3rem] bg-[#070B16] overflow-hidden relative">
                        <div className="flex items-center justify-between px-6 pt-3 pb-1">
                            <span className="text-[10px] font-semibold text-slate-300">9:41</span>
                            <div className="w-20 h-5 bg-black rounded-full border border-white/10" />
                            <div className="flex items-center gap-1 text-slate-400">
                                <Signal className="w-3 h-3" />
                                <Wifi className="w-3 h-3" />
                                <BatteryFull className="w-3.5 h-3.5" />
                            </div>
                        </div>
                        <div className="flex items-center gap-2.5 px-4 py-3 border-b border-white/5">
                            <div className="relative">
                                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-400 to-blue-600 flex items-center justify-center text-[10px] font-bold text-white ff-display">
                                    AI
                                </div>
                                <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-400 border-2 border-[#070B16]" />
                            </div>
                            <div>
                                <p className="text-[12px] font-semibold text-white leading-tight">{headerTitle}</p>
                                <p className="text-[10px] text-emerald-400">Online · replies instantly</p>
                            </div>
                        </div>
                        <div ref={scrollRef} className="h-[340px] sm:h-[380px] overflow-y-auto no-scrollbar px-3.5 py-4 flex flex-col gap-2.5">
                            {msgs.map((m, i) => (
                                <Bubble key={`${i}-${m.text.slice(0, 8)}`} m={m} />
                            ))}
                            {typing && <Typing />}
                        </div>
                        <div className="px-4 pb-5 pt-2">
                            <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-4 py-2.5">
                                <Plus className="w-4 h-4 text-slate-500" />
                                <span className="text-[11px] text-slate-500 flex-1">Text message</span>
                                <Mic className="w-4 h-4 text-slate-500" />
                            </div>
                        </div>
                        <div className="absolute inset-0 pointer-events-none bg-gradient-to-br from-white/[0.07] via-transparent to-transparent rounded-[2.3rem]" />
                    </div>
                </div>
                <div className="mx-auto mt-6 w-40 h-5 bg-black/60 blur-xl rounded-full" />
            </motion.div>
        </div>
    );
};
